import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Button, List, ListItem, ListItemText, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { LeaveWithoutSavingDialog } from 'components/common';
import { MARKETING_PROCESS, MarketingProcessCode } from '../../../constants';
import type { PackEntry } from 'types/shared';
import { isPackBasedMarketingProcess, isScalingEgProcess, } from '../utils/marketingProcess';
import { resolveTestCreativesViewState } from '../utils/savedCreativeFilters';
import { useLocationParams } from 'hooks';
import { SINGLE_AD_TYPE } from '../constants';

const createDefaultPack = (adType = '') => ({
  tag: '',
  adType,
  creatives: [] as string[],
  filterCreativeByName: [] as string[],
  visual_touch_ups: true,
});

const isHypothesisTesting = (code: MarketingProcessCode): boolean =>
  code === MarketingProcessCode.HYPOTHESIS_TESTING;

const shouldClearCreativesOnSwitch = (
  from: MarketingProcessCode,
  to: MarketingProcessCode,
): boolean => {
  if (from === to) return false;

  if (isPackBasedMarketingProcess(from) && isPackBasedMarketingProcess(to)) {
    return false;
  }

  if (isHypothesisTesting(from) || isHypothesisTesting(to)) return true;

  return (isScalingEgProcess(from) || isScalingEgProcess(to));
};

/** Leaving HT clears ad type; entering HT always uses single_ad. */
const shouldResetAdTypeOnSwitch = (
  from: MarketingProcessCode,
  to: MarketingProcessCode,
): boolean => isHypothesisTesting(from) || isHypothesisTesting(to);

const resolveNextAdType = (
  to: MarketingProcessCode,
  resetAdType: boolean,
  currentAdType: string,
  activePackAdType: string,
): string => {
  if (isHypothesisTesting(to)) return SINGLE_AD_TYPE;
  if (resetAdType) return '';
  return currentAdType || activePackAdType;
};

const hasCreativeSelections = (
  packsWithCreatives: PackEntry[],
  selectedCreatives: string[],
  marketingProcess: MarketingProcessCode | undefined,
): boolean => {
  if (isScalingEgProcess(marketingProcess)) {
    return selectedCreatives.length > 0;
  }

  const hasPackCreatives = packsWithCreatives.some((pack) => pack.creatives.length > 0);

  return hasPackCreatives || selectedCreatives.length > 0;
};

interface MarketingProcessTabsProps {
  onProcessChange?: () => void;
}

const MarketingProcessTabs: FC<MarketingProcessTabsProps> = ({ onProcessChange }) => {
  const { watch, setValue, getValues } = useFormContext();
  const marketingProcess = watch('marketingProcess') as MarketingProcessCode | undefined;
  const {
    marketingProcess: marketingProcessParam,
    adType: adTypeParam,
    setMarketingProcess: setMarketingProcessParam,
    setAdType: setAdTypeParam,
  } = useLocationParams();

  const [pendingProcess, setPendingProcess] = useState<MarketingProcessCode | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const applyMarketingProcessChange = useCallback(
    (code: MarketingProcessCode, clearCreatives: boolean, resetAdType: boolean) => {
      const currentAdType = String(getValues('adType') ?? '');
      const activePackIndex = getValues('activePacksWithCreatives') as number | null;
      const packsWithCreatives =
        (getValues('packsWithCreatives') as PackEntry[] | undefined) ?? [];
      const activePackAdType =
        typeof activePackIndex === 'number'
          ? (packsWithCreatives[activePackIndex]?.adType ?? '')
          : '';
      const nextAdType = resolveNextAdType(
        code,
        resetAdType,
        currentAdType,
        activePackAdType,
      );

      setMarketingProcessParam(code);
      setValue('marketingProcess', code, { shouldValidate: true, shouldDirty: true });
      setValue('selectedCampaigns', [], { shouldValidate: true });
      setValue('selectedAdSets', [], { shouldValidate: true });

      if (clearCreatives) {
        setValue('selectedCreatives', [], { shouldValidate: true });
        if (isScalingEgProcess(code)) {
          setValue('packsWithCreatives', [], { shouldValidate: true });
          setValue('activePacksWithCreatives', null, { shouldValidate: true });
        } else {
          setValue('packsWithCreatives', [createDefaultPack(nextAdType)], {
            shouldValidate: true,
          });
          setValue('activePacksWithCreatives', 0, { shouldValidate: true });
        }
        setValue('adType', nextAdType, { shouldValidate: true });
        if (isHypothesisTesting(code)) {
          setAdTypeParam(SINGLE_AD_TYPE);
        } else if (resetAdType) {
          setAdTypeParam(undefined);
        }
        setValue('filterCreativesByName', [], { shouldValidate: true });
        setValue(
          'testCreativesViewState',
          resolveTestCreativesViewState(getValues('app') as string | undefined),
        );
      } else if (isScalingEgProcess(code)) {
        setValue('packsWithCreatives', [], { shouldValidate: true });
        setValue('activePacksWithCreatives', null, { shouldValidate: true });
        if (nextAdType) {
          setValue('adType', nextAdType, { shouldValidate: true });
        }
      }
    },
    [getValues, setAdTypeParam, setMarketingProcessParam, setValue],
  );

  // Hypothesis testing always uses single_ad in the form and URL.
  useEffect(() => {
    if (!isHypothesisTesting(marketingProcessParam)) return;
    if (adTypeParam === SINGLE_AD_TYPE && getValues('adType') === SINGLE_AD_TYPE) return;

    setAdTypeParam(SINGLE_AD_TYPE);
    setValue('adType', SINGLE_AD_TYPE, { shouldValidate: true });
  }, [adTypeParam, getValues, marketingProcessParam, setAdTypeParam, setValue]);

  // Browser back/forward changes the URL param; mirror it into the form
  // without a confirmation dialog so history navigation always applies.
  useEffect(() => {
    if (!marketingProcess || marketingProcessParam === marketingProcess) return;

    applyMarketingProcessChange(
      marketingProcessParam,
      shouldClearCreativesOnSwitch(marketingProcess, marketingProcessParam),
      shouldResetAdTypeOnSwitch(marketingProcess, marketingProcessParam),
    );
  }, [applyMarketingProcessChange, marketingProcess, marketingProcessParam]);

  const handleTabChange = useCallback(
    (code: MarketingProcessCode) => {
      if (!marketingProcess || marketingProcess === code) return;

      const clearCreatives = shouldClearCreativesOnSwitch(marketingProcess, code);
      const resetAdType = shouldResetAdTypeOnSwitch(marketingProcess, code);

      if (clearCreatives) {
        const packsWithCreatives = (getValues('packsWithCreatives') as PackEntry[] | undefined) ?? [];
        const selectedCreatives = (getValues('selectedCreatives') as string[] | undefined) ?? [];

        if (hasCreativeSelections(packsWithCreatives, selectedCreatives, marketingProcess)) {
          setPendingProcess(code);
          setIsConfirmOpen(true);

          return;
        }
      }

      applyMarketingProcessChange(code, clearCreatives, resetAdType);
    },
    [applyMarketingProcessChange, getValues, marketingProcess],
  );

  const handleConfirmSwitch = useCallback(() => {
    if (!pendingProcess || !marketingProcess) return;

    applyMarketingProcessChange(
      pendingProcess,
      true,
      shouldResetAdTypeOnSwitch(marketingProcess, pendingProcess),
    );
    onProcessChange?.();
    setPendingProcess(null);
    setIsConfirmOpen(false);
  }, [applyMarketingProcessChange, marketingProcess, onProcessChange, pendingProcess]);

  const handleCancelSwitch = useCallback(() => {
    setPendingProcess(null);
    setIsConfirmOpen(false);
  }, []);

  return (
    <>
      <List
        sx={{
          display: 'flex',
          alignItems: 'end',
          justifyContent: 'start',
          flexDirection: 'row',
          width: '100%',
          mb: -2,
          p: 0,
        }}
      >
        {MARKETING_PROCESS.map(({ name, code }) => {
          const isActive = marketingProcess === code;

          return (
            <ListItem key={code} sx={{ px: 0, pt: .75, pb: 0, width: 'auto' }}>
              <Button
                variant="text"
                disableElevation
                disableRipple={isActive}
                disableTouchRipple
                disableFocusRipple
                onClick={() => {
                  handleTabChange(code);
                }}
                sx={(t) => ({
                  display: 'flex',
                  gap: 1,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 1,
                  px: 2,
                  pl: 2.5,
                  pb: 2.5,
                  fontSize: t.typography.caption.fontSize,
                  textTransform: 'capitalize',
                  borderRadius: '8px 8px 0 0',
                  color: isActive ? t.palette.primary.main : t.palette.text.secondary,
                  backgroundColor: isActive ? 'white' : 'transparent',
                  position: 'relative',
                  transition: 'none',
                  '&::after': {
                    content: isActive ? '""' : 'none',
                    position: 'absolute',
                    right: '-19px',
                    top: '2px',
                    width: '21px',
                    height: 'calc(100% - 2px)',
                    backgroundColor: 'white',
                    clipPath: 'polygon(0 0, 100% 100%, 0 100%)',
                  },
                  '&:hover': {
                    color: t.palette.primary.main,
                    backgroundColor: isActive ? 'white' : 'transparent',
                  },
                })}
              >
                <ListItemText
                  primary={
                    <Typography
                      sx={(t) => ({
                        fontSize: t.typography.body2.fontSize,
                        fontWeight: 500,
                        letterSpacing: '0.4px',
                        whiteSpace: 'nowrap',
                      })}
                    >
                      {name}
                    </Typography>
                  }
                />
              </Button>
            </ListItem>
          );
        })}
      </List>
      <LeaveWithoutSavingDialog
        open={isConfirmOpen}
        onStay={handleCancelSwitch}
        onLeave={handleConfirmSwitch}
      />
    </>
  );
};

export default MarketingProcessTabs;
